import { getLocalAppSession } from './storageHelpers';
import { capitalize, textToHtml } from './baseHelpers';
import { generatePromptKeywords } from './vacancy-generator/prompts';
import { authHeader } from './usableAuthToken';

type ChatGPTAgent = 'user' | 'system' | 'assistant';

export interface ChatGPTMessage {
  role: ChatGPTAgent;
  content: string;
}

interface OpenAIThreadsPayload {
  identifier: string;
  messages: ChatGPTMessage[];
  thread_id?: string;
  vacancy_id: number;
  stream?: boolean;
}

type OpenAIThreadsResponse = { thread_id: string; response: string };

/**
 * The server ends an answer that went wrong with a line of its own,
 * "error:<reason>" (`AiGenerationController.php`, the three `echo "\nerror:`
 * lines). Two of them mean there is no usable answer. The third,
 * "storage_failed", comes AFTER a complete answer, when the server could not
 * file its own record of it: the answer is good and is kept.
 * It is a signal, not words for the page: written into the editor it read
 * "error:server_exception", and auto-save stored that as the job text two
 * seconds later (found by Steven by hand, 3 Oct 2026).
 */
export class AiAnswerFailed extends Error {
  constructor(public reason: string) {
    super(`The AI answer failed: ${reason}`);
    this.name = 'AiAnswerFailed';
  }
}

const FAILURE_LINE = /(?:^|\n)error:([^\n]*)\n?$/;

/** The answer so far, without a failure line that may be forming at its end. */
const withoutFailureTail = (text: string): string => {
  if (text.startsWith('error:')) return '';
  const at = text.lastIndexOf('\nerror:');
  return at === -1 ? text : text.slice(0, at);
};
// A real Error that names the status. statusText alone is the empty string
// over HTTP/2, which reached Sentry as "No error message" (SYM-6753).
const generateFailure = ({ ok, status, statusText }: Response) =>
  new Error(
    ok
      ? `AI generate failed: HTTP ${status} with an empty body`
      : `AI generate failed: HTTP ${status}${
          statusText ? ` ${statusText}` : ''
        }`
  );

export const getOpenAiThreadsResponse = ({
  onChange,
  signal,
  ...body
}: OpenAIThreadsPayload & {
  onChange?: (resp: string) => void;
  signal?: AbortSignal;
}) =>
  new Promise<OpenAIThreadsResponse>(async (resolve, reject) => {
    let response: Response;
    try {
      response = await fetch(
        `${import.meta.env.VITE_BASE_URL_API}/v4/ai/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Active-Customer': `${getLocalAppSession()?.activeCustomer}`,
            ...(await authHeader()),
          },
          body: JSON.stringify(body),
          signal,
        }
      );
    } catch (error) {
      // The line dropped, or the request was stopped. This used to wait for
      // ever: nothing passed the failure on (review round 5, C4).
      reject(error);
      return;
    }

    if (!response.ok || !response.body) {
      reject(generateFailure(response));
      return;
    }

    if (!body.stream || !onChange) {
      // A reply that cannot be read is a failure. Unguarded, it left this
      // promise neither kept nor broken, and whoever waited on it waited for
      // ever (review round 6, E4).
      try {
        const result = await response.json();
        resolve(result?.data);
      } catch (error) {
        reject(error);
      }
      return;
    }

    try {
      const output = await handleThreadsStream(response.body, (value) =>
        onChange(value)
      );
      resolve(output);
    } catch (error) {
      reject(error);
    }
  });

/**
 * How long the keyword request may take. It only decides which words are set
 * in bold; an answer is not held up for it, nor lost over it.
 */
export const FORMATTING_LIMIT_MS = 20_000;

export const getFormattedText = async (
  text: string,
  vacancyId: number,
  identifier: string,
  threadId: string
) => {
  // Remove possible new line(s) from start of output
  if (text?.startsWith('\n')) text = text.slice(1);
  if (text?.startsWith('\n')) text = text.slice(1);
  if (text?.trim().length === 1) text = text.slice(1);
  if (text?.startsWith('\n')) text = text.slice(1);

  // Convert to html. This is the answer whatever happens below: without the
  // keywords it is the same text, in paragraphs, with nothing in bold. It used
  // to fall back to the RAW text, which the page then showed as one run-on
  // block (review round 8).
  let html = textToHtml(text ?? '');

  try {
    let limit: number | undefined;
    const tooLate = new Promise<never>((_, reject) => {
      limit = window.setTimeout(
        () => reject(new Error('The keyword request took too long.')),
        FORMATTING_LIMIT_MS
      );
    });
    const { response } = await Promise.race([
      getOpenAiThreadsResponse({
        identifier,
        vacancy_id: vacancyId,
        messages: [generatePromptKeywords()],
        thread_id: threadId,
        stream: false,
      }),
      tooLate,
    ]).finally(() => window.clearTimeout(limit));

    // Get keywords from keywords output
    const keywords = response
      ?.split('\n')
      .map((word) => word.trim())
      .filter((word) => word) ?? [''];

    // Remove the numbering
    let formattedKeywords = keywords.flatMap(
      (word) => word.match(/[a-zA-Z]+/g) ?? ['']
    );
    const blacklistedKeywords = ['en'];

    formattedKeywords = formattedKeywords.filter(
      (word) => !blacklistedKeywords.includes(word)
    );

    // Extend array by adding duplicates of word in different casings
    formattedKeywords = [
      ...formattedKeywords,
      ...formattedKeywords.map((word) => capitalize(word)),
      ...formattedKeywords.map((word) => word.toLowerCase()),
    ];

    // If keywords were found, wrap them in strong tags
    formattedKeywords.forEach((word) => {
      html = html?.replaceAll(` ${word} `, ` <strong>${word}</strong> `);
      html = html?.replaceAll(` ${word},`, ` <strong>${word}</strong>,`);
      html = html?.replaceAll(` ${word}.`, ` <strong>${word}</strong>.`);
    });

    return html;
  } catch (error: any) {
    console.error(error);
    return html;
  }
};

const METADATA_PREFIXES = ['thread_id:', 'conversation_id:'] as const;

export const handleThreadsStream = async (
  stream: ReadableStream<Uint8Array>,
  onProgress: (value: string) => void
): Promise<OpenAIThreadsResponse> => {
  const reader = stream.getReader();
  const decoder = new TextDecoder('utf-8');
  let chunkValue = '';
  let pending = '';
  let threadId = '';
  let isReadingMetadata = true;
  /** What the caller has been shown so far. */
  let shown = '';

  const appendText = (text: string) => {
    if (text === '') return;

    chunkValue += text;
    const visible = withoutFailureTail(chunkValue);
    if (visible !== '' && visible !== shown) {
      shown = visible;
      onProgress(visible);
    }
  };

  const consumePending = (isFinalChunk: boolean) => {
    while (isReadingMetadata) {
      const prefix = METADATA_PREFIXES.find((candidate) =>
        pending.startsWith(candidate)
      );

      if (!prefix) {
        const isPartialMetadataPrefix = METADATA_PREFIXES.some((candidate) =>
          candidate.startsWith(pending)
        );

        if (isPartialMetadataPrefix && !isFinalChunk) return;

        isReadingMetadata = false;
        appendText(pending);
        pending = '';
        return;
      }

      const newlineIndex = pending.indexOf('\n');
      if (newlineIndex === -1) {
        if (!isFinalChunk) return;

        const id = pending.slice(prefix.length).trim();
        if (id !== '' && (prefix === 'thread_id:' || threadId === '')) {
          threadId = id;
        }
        pending = '';
        return;
      }

      const id = pending.slice(prefix.length, newlineIndex).trim();
      if (id !== '' && (prefix === 'thread_id:' || threadId === '')) {
        threadId = id;
      }
      pending = pending.slice(newlineIndex + 1);
    }

    appendText(pending);
    pending = '';
  };

  while (true) {
    const { value, done } = await reader.read();
    pending += decoder.decode(value, { stream: !done });
    consumePending(done);

    if (done) break;
  }

  pending += decoder.decode();
  consumePending(true);

  const failure = chunkValue.match(FAILURE_LINE);
  if (failure) {
    const reason = failure[1].trim();
    const answer = withoutFailureTail(chunkValue);
    if (reason === 'storage_failed' && answer !== '') {
      return { thread_id: threadId, response: answer };
    }
    throw new AiAnswerFailed(reason);
  }

  // Something that only LOOKED like a failure line forming was held back.
  if (chunkValue !== shown && chunkValue !== '') onProgress(chunkValue);

  return { thread_id: threadId, response: chunkValue };
};
